import React, { useState, useContext, useEffect } from 'react';
import {
    Checkbox, FormControlLabel,
    Grid, makeStyles, TextField, MenuItem,
} from '@material-ui/core';
import {Autocomplete} from "@material-ui/lab";
import OrderStatusesContext from "./Contexts/OrderStatusesContext";
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import {useTranslation} from "react-i18next";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const useStyles = makeStyles(() => ({
    checkboxFilter: {
        marginTop: '10px'
    }
}));

// Date range options for the period selector
const DATE_RANGE_OPTIONS = [
    { value: 'custom', label: 'Vlastné' },
    { value: 'last_1_day', label: 'Posledný deň' },
    { value: 'last_2_days', label: 'Posledné 2 dni' },
    { value: 'last_5_days', label: 'Posledných 5 dní' },
    { value: 'last_10_days', label: 'Posledných 10 dní' },
];

const TableFilter = ({ className, enabled, setFilterValue, filter, ...rest }) => {
    const orderStatusOptions = useContext(OrderStatusesContext);
    const {t} = useTranslation('connector');
    const classes = useStyles();
    const [opened, setOpened] = useState(false);

    const [orderStatuses, setOrderStatuses] = useState([]);
    const [hideExported, setHideExported] = useState(false);
    const [dateRange, setDateRange] = useState('last_1_day');

    // Initialize values from filter (on filter change or options load)
    useEffect(() => {
        if (orderStatusOptions && orderStatusOptions.length > 0) {
            // Set statuses from filter if they exist
            if (filter.statuses && filter.statuses.length > 0) {
                const selectedStatuses = orderStatusOptions.filter(status => 
                    filter.statuses.includes(status.id)
                );
                setOrderStatuses(selectedStatuses);
            } else if (filter.statuses && filter.statuses.length === 0) {
                // Explicitly empty array means "no statuses"
                setOrderStatuses([]);
            }
        }
        
        // Set hide_exported from filter
        if (filter.hide_exported !== undefined) {
            setHideExported(filter.hide_exported);
        } else {
            setHideExported(false);
        }

        // Set date_range from filter
        if (filter.date_range !== undefined) {
            setDateRange(filter.date_range);
        } else {
            setDateRange('last_1_day');
        }
    }, [filter.statuses, filter.hide_exported, filter.date_range, orderStatusOptions]);

    // Reset on filter clear
    useEffect(() => {
        if (Object.keys(filter).length === 0) {
            setOrderStatuses([]);
            setHideExported(false);
            setDateRange('last_1_day');
        }
    }, [filter]);

    const onOpen = () => {
        setOpened(true);
    }

    const handleDateRangeChange = (event) => {
        const newValue = event.target.value;
        setDateRange(newValue);
        setFilterValue('date_range', newValue);
    }

    return (
        <Grid container spacing={3}>
            {
                enabled.date_range &&
                <Grid item xs={12} md={6} lg={4}>
                    <TextField
                        select
                        fullWidth
                        variant="standard"
                        label="Objednávky za obdobie"
                        value={dateRange}
                        onChange={handleDateRangeChange}
                    >
                        {DATE_RANGE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
            }
            {
                enabled.order_status &&
                <Grid item xs={12} md={6} lg={4}>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={orderStatusOptions || []}
                        getOptionLabel={(option) => option.name}
                        onChange={(event, selected, reason) => {
                            setOrderStatuses(selected);
                            if (reason === 'remove-option' && !opened) {
                                setFilterValue('statuses', selected.map(val => val.id));
                            }
                        }}
                        onOpen={onOpen}
                        onClose={() => {
                            setOpened(false);
                            setFilterValue('statuses', orderStatuses.map(val => val.id));
                        }}
                        loading={!!orderStatusOptions}
                        value={orderStatuses}
                        defaultValue={[]}
                        renderOption={(option, { selected }) => (
                            <React.Fragment>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selected}
                                />
                                {option.name}
                            </React.Fragment>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                label={t('label.statuses')}
                                fullWidth
                            />
                        )}
                    />
                </Grid>
            }
            {
                enabled.hide_exported &&
                <Grid item xs={12} md={6} lg={4}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={hideExported}
                                onChange={(event) => {
                                    setHideExported(event.target.checked);
                                    setFilterValue('hide_exported', event.target.checked)
                                }}
                            />
                        }
                        label={t('label.hide_exported')}
                    />
                </Grid>
            }
        </Grid>
    );
}

export default TableFilter;